import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();

  const [language, setLanguageState] = useState(() => {
    // Get from localStorage or default to 'de'
    const savedLang = localStorage.getItem('language');
    if (savedLang) return savedLang;

    // Check browser language
    const browserLang = navigator.language.split('-')[0];
    if (browserLang === 'en') return 'en';
    return 'de';
  });

  useEffect(() => {
    // Save to localStorage
    localStorage.setItem('language', language);

    // Update i18n
    i18n.changeLanguage(language);

    // Update document lang attribute
    document.documentElement.lang = language;
  }, [language, i18n]);

  const setLanguage = (newLang) => {
    if (newLang === 'de' || newLang === 'en') {
      setLanguageState(newLang);
    }
  };

  const toggleLanguage = () => {
    setLanguageState(prev => prev === 'de' ? 'en' : 'de');
  };

  const value = {
    language,
    isGerman: language === 'de',
    isEnglish: language === 'en',
    setLanguage,
    toggleLanguage,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export default LanguageContext;
